<template>
  <v-data-table show-expand
                class="elevation-1 mt-5 rounded-card card-shadow"
                no-data-text="Nenhuma compra encontrada"
                loading-text="Carregando compras..."
                :loading="loading"
                :server-items-length="compras.total"
                :footer-props="footerOptions"
                :options="optionsList"
                :headers="headers"
                :items="compras.content"
                :expanded.sync="expanded"
                @update:options="updateOptions">
    <template v-slot:item.id="{ item }">
      <div class="text-center">
        <px-cod-interno-cod-proxpect-compra :compra="item"/>
      </div>
    </template>
    <template v-slot:item.fornecedor.nome="{ item }">
      <div v-if="item.fornecedor">
        <px-situacao-compra-icon :situacao="item.situacao"/>
        <px-span-tooltip top
                         :text="labelFornecedor(item)"
                         :tooltip="tooltipFornecedor(item)"/>
      </div>
      <div v-else>
        <px-situacao-compra-icon :situacao="item.situacao"/>
          -
      </div>
    </template>
    <template v-slot:item.comprador="{ item }">
      <px-span-tooltip top :text="comprador(item)" :tooltip="item.comprador.pessoa.nome"/>
    </template>
    <template v-slot:item.dataCompra="{ item }">
      <div v-if="item.dataCompra">
        {{ item.dataCompra | date }}
      </div>
      <div v-else>
          -
      </div>
    </template>
    <template v-slot:item.dataFaturamento="{ item }">
      <div v-if="item.dataFaturamento">
        {{ item.dataFaturamento | date }}
      </div>
      <div v-else>
        -
      </div>
    </template>
    <template v-slot:item.dataPrevisaoEntrega="{ item }">
      <compra-previsao-entrega v-if="item.dataPrevisaoEntrega"
                               @change="emitUpdate"
                               :compra="item"/>
      <div v-else>
        -
      </div>
    </template>
    <template v-slot:item.quantidadeProdutos="{ item }">
      {{ item.quantidadeProdutos }} produto(s)
    </template>
    <template v-slot:item.saving="{ item }">
      {{ item.valorMoedaConvertido.saving | currency }}
      ({{ percentualSaving(item) | number('0.00') }}%)
      <div class="caption" v-if="isMoedaEstrangeiraFn(item.moeda)">
        {{ item.saving | currency(simboloMoedaFn(item.moeda),
        2,
        configuracaoFormatacaoMoedaFn(item.moeda)) }}
      </div>
    </template>
    <template v-slot:item.total="{ item }">
      {{ item.valorMoedaConvertido.total | currency }}
      <px-moeda-chip v-if="isMoedaEstrangeiraFn(item.moeda)" :moeda="item.moeda"/>
      <div class="caption" v-if="isMoedaEstrangeiraFn(item.moeda)">
        {{ item.total | currency(simboloMoedaFn(item.moeda),
          2,
        configuracaoFormatacaoMoedaFn(item.moeda)) }}
      </div>
    </template>
    <template v-slot:item.options="{ item }">
      <div class="text-right">
        <px-show-saving :compra="item" small/>
        <v-btn v-if="!isRascunho(item)" icon @click="goToCompra(item)">
          <v-icon>mdi-eye</v-icon>
        </v-btn>
        <lista-compra-menu v-if="hasRoleComprador"
                           :item="item"
                           @remove="remove"
                           @remove-rascunho="removeRascunho"
                           @update="emitUpdate"/>
      </div>
    </template>
    <template v-slot:expanded-item="{ item }">
      <td :colspan="headers.length">
        <listagem-compra-detail :compra="item"/>
      </td>
    </template>
  </v-data-table>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import currency from 'currency.js';
import { SituacaoCompra } from 'px-business-components';
import CompraPrevisaoEntrega from '@/modules/compra/CompraPrevisaoEntrega.vue';
import moedaMixin from '@/mixins/moeda-mixin';
import ListaCompraMenu from './ListaCompraMenu.vue';
import ListagemCompraDetail from './ListagemCompraDetail.vue';

export default {
  mixins: [moedaMixin],
  components: { ListagemCompraDetail, CompraPrevisaoEntrega, ListaCompraMenu },
  props: {
    compras: Object,
    default: [],
    loading: Boolean,
    onChangeOptions: {
      type: Function,
      required: true,
    },
    optionsList: Object,
  },
  data() {
    return {
      expanded: [],
      footerOptions: {
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        itemsPerPageText: 'Linhas por página',
      },
      headers: [
        {
          text: 'Código',
          sortable: false,
          value: 'id',
        },
        {
          text: 'Fornecedor',
          value: 'fornecedor.nome',
        },
        {
          text: 'Empresa',
          value: 'comprador',
        },
        {
          text: 'Realizado em',
          value: 'dataCompra',
        },
        {
          text: 'Faturado em',
          value: 'dataFaturamento',
        },
        {
          text: 'Entregue em',
          value: 'dataPrevisaoEntrega',
        },
        {
          text: 'Total',
          value: 'total',
        },
        {
          text: null,
          sortable: false,
          value: 'options',
        },
        {
          text: '',
          value: 'data-table-expand',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('user', ['hasRoleComprador', 'hasRoleGestorCompras']),
    percentualSaving() {
      return (item) => {
        if (!item.saving) {
          return 0;
        }
        return currency(item.saving).multiply(100)
          .divide(currency(item.total).add(item.saving))
          .value;
      };
    },
    isRascunho() {
      return item => SituacaoCompra.keys.RASCUNHO.key === item.situacao;
    },
    comprador() {
      return item => Vue.filter('truncate')(item.comprador.pessoa.nome, 15);
    },
    labelFornecedor() {
      return (compra) => {
        const nome = compra.nomeInternoFornecedor || compra.fornecedor.pessoa.nome;
        return Vue.filter('truncate')(nome, 15);
      };
    },
    tooltipFornecedor() {
      return compra => compra.nomeInternoFornecedor || compra.fornecedor.pessoa.nome;
    },
  },
  methods: {
    emitUpdate() {
      this.$emit('update');
    },
    updateOptions(options) {
      this.onChangeOptions(options);
    },
    remove(item) {
      this.$emit('remove', item);
    },
    removeRascunho(item) {
      this.$emit('remove-rascunho', item);
    },
    goToCompra(item) {
      this.$router.push({
        name: 'main.compras.detalhes',
        params: {
          id: item.id,
        },
      });
    },
  },
};
</script>
